'use client'

import { forwardRef, ReactNode, useCallback } from 'react'
import { FileText, Users, DollarSign, Calendar, Search, Plus, RefreshCw, AlertCircle } from 'lucide-react'

/**
 * BIRHAUS Empty State Component
 * 
 * Enhanced empty state for zero-data scenarios implementing BIRHAUS principles:
 * - Spanish-first messages ("No hay miembros registrados")
 * - Helpful action suggestions with max 2 actions (cognitive load)
 * - Appropriate icons for context
 * - Progressive disclosure for help information
 * - Accessibility compliant (WCAG AA+)
 * - IPU Admin specific presets for common scenarios
 * - Consistent visual hierarchy
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausEmptyStateAction {
  label: string
  labelEs?: string
  labelEn?: string
  onClick: () => void | Promise<void>
  variant?: 'primary' | 'secondary'
  icon?: ReactNode
  loading?: boolean
}

export interface BirhausEmptyStateProps {
  // BIRHAUS Principle #7: Bilingual by design
  title?: string
  titleEs?: string
  titleEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  showHelpText?: boolean
  
  // Visual elements
  icon?: ReactNode
  illustration?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  
  // BIRHAUS Principle #3: Max 2 actions (cognitive load)
  actions?: BirhausEmptyStateAction[]
  
  // Predefined scenarios for IPU Admin
  scenario?: 'members' | 'donations' | 'events' | 'search' | 'reports' | 'general'
  
  // State indicators
  loading?: boolean
  error?: boolean
  
  // BIRHAUS Principle #6: Accessibility = dignity
  role?: string
  
  // Locale
  locale?: 'es' | 'en'
  
  // Standard props
  className?: string
  children?: ReactNode
}

export const BirhausEmptyState = forwardRef<HTMLDivElement, BirhausEmptyStateProps>(({
  // Content (Spanish-first)
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  
  // Progressive disclosure
  helpText,
  helpTextEs,
  helpTextEn,
  showHelpText = false,
  
  // Visual elements
  icon,
  illustration,
  size = 'md',
  
  // Actions (BIRHAUS: Max 2)
  actions = [],
  
  // Predefined scenarios
  scenario,
  
  // State
  loading = false,
  error = false,
  
  // Accessibility
  role = 'region',
  
  // Locale
  locale = 'es',
  
  // Standard props
  className,
  children,
  ...props
}, ref) => {
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  // Predefined scenarios for IPU Admin
  const scenarioConfig = {
    members: {
      icon: Users,
      titleEs: 'No hay miembros registrados',
      titleEn: 'No members registered',
      descriptionEs: 'Comienza agregando el primer miembro de tu iglesia.',
      descriptionEn: 'Start by adding the first member of your church.',
      helpTextEs: 'Los miembros son el corazón de tu iglesia. Aquí podrás registrar información personal, historial de participación y roles ministeriales.',
      helpTextEn: 'Members are the heart of your church. Here you can register personal information, participation history and ministerial roles.',
      defaultActions: [
        {
          labelEs: 'Agregar Miembro',
          labelEn: 'Add Member',
          variant: 'primary' as const,
          icon: <Plus className="h-4 w-4" />
        }
      ]
    },
    donations: {
      icon: DollarSign,
      titleEs: 'No hay donaciones registradas',
      titleEn: 'No donations recorded',
      descriptionEs: 'Registra las primeras donaciones y ofrendas de tu iglesia.',
      descriptionEn: 'Record the first donations and offerings from your church.',
      helpTextEs: 'El registro de donaciones te permite mantener un control financiero transparente y generar recibos para los donantes.',
      helpTextEn: 'Donation recording allows you to maintain transparent financial control and generate receipts for donors.',
      defaultActions: [
        {
          labelEs: 'Registrar Donación',
          labelEn: 'Record Donation',
          variant: 'primary' as const,
          icon: <Plus className="h-4 w-4" />
        }
      ]
    },
    events: {
      icon: Calendar,
      titleEs: 'No hay eventos programados',
      titleEn: 'No events scheduled',
      descriptionEs: 'Crea el primer evento para tu congregación.',
      descriptionEn: 'Create the first event for your congregation.',
      helpTextEs: 'Los eventos te ayudan a organizar cultos, reuniones especiales, actividades sociales y programas ministeriales.',
      helpTextEn: 'Events help you organize services, special meetings, social activities and ministerial programs.',
      defaultActions: [
        {
          labelEs: 'Crear Evento',
          labelEn: 'Create Event',
          variant: 'primary' as const,
          icon: <Plus className="h-4 w-4" />
        }
      ]
    },
    search: {
      icon: Search,
      titleEs: 'No se encontraron resultados',
      titleEn: 'No results found',
      descriptionEs: 'Intenta con otros términos de búsqueda o verifica la ortografía.',
      descriptionEn: 'Try different search terms or check spelling.',
      helpTextEs: 'Puedes buscar por nombre, apellido, teléfono, email o cualquier información relacionada con los miembros.',
      helpTextEn: 'You can search by name, surname, phone, email or any information related to members.',
      defaultActions: [
        {
          labelEs: 'Limpiar Filtros',
          labelEn: 'Clear Filters',
          variant: 'secondary' as const,
          icon: <RefreshCw className="h-4 w-4" />
        }
      ]
    },
    reports: {
      icon: FileText,
      titleEs: 'No hay datos para el reporte',
      titleEn: 'No data for report',
      descriptionEs: 'No hay información suficiente para generar este reporte.',
      descriptionEn: 'There is not enough information to generate this report.',
      helpTextEs: 'Los reportes se generan automáticamente cuando tienes datos registrados en el sistema.',
      helpTextEn: 'Reports are generated automatically when you have data registered in the system.',
      defaultActions: [
        {
          labelEs: 'Cambiar Período',
          labelEn: 'Change Period',
          variant: 'secondary' as const,
          icon: <Calendar className="h-4 w-4" />
        }
      ]
    },
    general: {
      icon: AlertCircle,
      titleEs: 'No hay elementos',
      titleEn: 'No items',
      descriptionEs: 'No se encontraron elementos en esta sección.',
      descriptionEn: 'No items found in this section.',
      helpTextEs: 'Esta sección se poblará automáticamente cuando agregues contenido relevante.',
      helpTextEn: 'This section will populate automatically when you add relevant content.',
      defaultActions: []
    }
  }
  
  // Get configuration for scenario
  const config = scenario ? scenarioConfig[scenario] : null
  
  // Resolve content with scenario fallbacks
  const displayTitle = getDisplayText(
    title,
    titleEs || config?.titleEs,
    titleEn || config?.titleEn,
    config?.titleEs || 'Sin contenido'
  )
  
  const displayDescription = getDisplayText(
    description,
    descriptionEs || config?.descriptionEs,
    descriptionEn || config?.descriptionEn,
    config?.descriptionEs || ''
  )
  
  const displayHelpText = getDisplayText(
    helpText,
    helpTextEs || config?.helpTextEs,
    helpTextEn || config?.helpTextEn,
    config?.helpTextEs || ''
  )
  
  // Use provided icon or scenario icon
  const IconComponent = icon ? () => icon : config?.icon
  
  // Combine provided actions with scenario defaults
  const allActions = [...actions, ...(config?.defaultActions || [])]
  
  // BIRHAUS: Limit to max 2 actions for cognitive load
  const limitedActions = allActions.slice(0, 2)
  
  // Size classes
  const sizeClasses = {
    sm: {
      container: 'py-8 px-4',
      icon: 'h-8 w-8',
      title: 'text-lg',
      description: 'text-sm',
      spacing: 'space-y-3'
    },
    md: {
      container: 'py-12 px-6',
      icon: 'h-12 w-12',
      title: 'text-xl',
      description: 'text-base',
      spacing: 'space-y-4'
    },
    lg: {
      container: 'py-16 px-8',
      icon: 'h-16 w-16',
      title: 'text-2xl',
      description: 'text-lg',
      spacing: 'space-y-6'
    }
  }
  
  const sizeConfig = sizeClasses[size]
  
  // Loading state
  if (loading) {
    return (
      <div
        ref={ref}
        role={role}
        className={cn(
          'birhaus-empty-state flex flex-col items-center justify-center text-center',
          sizeConfig.container,
          sizeConfig.spacing,
          className
        )}
        {...props}
      >
        <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-primary-600 rounded-full" />
        <p className="text-gray-600">
          {locale === 'es' ? 'Cargando...' : 'Loading...'}
        </p>
      </div>
    )
  }
  
  // Error state
  if (error) {
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'birhaus-empty-state flex flex-col items-center justify-center text-center',
          sizeConfig.container,
          sizeConfig.spacing,
          className
        )}
        {...props}
      >
        <AlertCircle className={cn('text-red-500', sizeConfig.icon)} />
        <div>
          <h3 className={cn('font-semibold text-red-800', sizeConfig.title)}>
            {locale === 'es' ? 'Error al cargar' : 'Error loading'}
          </h3>
          <p className={cn('text-red-600 mt-1', sizeConfig.description)}>
            {locale === 'es' 
              ? 'No se pudo cargar el contenido. Intenta nuevamente.' 
              : 'Could not load content. Please try again.'
            }
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>{locale === 'es' ? 'Reintentar' : 'Retry'}</span>
        </button>
      </div>
    )
  }
  
  return (
    <div
      ref={ref}
      role={role}
      aria-label={displayTitle}
      className={cn(
        'birhaus-empty-state flex flex-col items-center justify-center text-center',
        sizeConfig.container,
        sizeConfig.spacing,
        className
      )}
      {...props}
    >
      {/* Illustration or Icon */}
      {illustration ? (
        <div className="flex-shrink-0">
          {illustration}
        </div>
      ) : IconComponent ? (
        <div className="flex-shrink-0">
          <IconComponent className={cn('text-gray-400', sizeConfig.icon)} />
        </div>
      ) : null}
      
      {/* Content */}
      <div className="max-w-md mx-auto">
        {/* Title */}
        <h3 className={cn('font-semibold text-gray-900', sizeConfig.title)}>
          {displayTitle}
        </h3>
        
        {/* Description */}
        {displayDescription && (
          <p className={cn('text-gray-600 mt-1', sizeConfig.description)}>
            {displayDescription}
          </p>
        )}
        
        {/* Custom Content */}
        {children && (
          <div className="mt-2">
            {children}
          </div>
        )}
      </div>
      
      {/* Actions */}
      {limitedActions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {limitedActions.map((action, index) => {
            const displayActionLabel = getDisplayText(
              'label' in action ? action.label : '',
              'labelEs' in action ? action.labelEs : '',
              'labelEn' in action ? action.labelEn : '',
              'label' in action ? action.label : 'Action'
            )
            const isPrimary = ('variant' in action ? action.variant : undefined) === 'primary' || index === 0
            
            return (
              <button
                key={index}
                type="button"
                onClick={'onClick' in action ? action.onClick : () => {}}
                disabled={'loading' in action ? action.loading : false}
                className={cn(
                  'inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md',
                  'transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
                  isPrimary ? 
                    'bg-primary-600 text-white hover:bg-primary-700' :
                    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                )}
              >
                {('loading' in action ? action.loading : false) && (
                  <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                )}
                {!('loading' in action ? action.loading : false) && ('icon' in action ? action.icon : null) && (
                  <span>{'icon' in action ? action.icon : null}</span>
                )}
                <span>{displayActionLabel}</span>
              </button>
            )
          })}
        </div>
      )}
      
      {/* Help Text */}
      {(showHelpText || size === 'lg') && displayHelpText && (
        <div className="max-w-lg mx-auto">
          <p className="text-sm text-gray-500 leading-relaxed">
            {displayHelpText}
          </p>
        </div>
      )}
    </div>
  )
})

BirhausEmptyState.displayName = 'BirhausEmptyState'

// Hook for managing empty state scenarios
export const useBirhausEmptyState = (
  data: any[] | null | undefined,
  loading: boolean = false,
  error: Error | null = null
) => {
  const isEmpty = !loading && !error && (!data || data.length === 0)
  const hasError = !loading && !!error
  const isLoading = loading
  
  return {
    isEmpty,
    hasError,
    isLoading,
    showEmptyState: isEmpty || hasError || isLoading,
    errorMessage: error?.message
  }
}

// Common empty state configurations for IPU Admin
export const birhausEmptyStatePresets = {
  // Members section
  noMembers: {
    scenario: 'members' as const,
    actions: [
      {
        labelEs: 'Agregar Primer Miembro',
        labelEn: 'Add First Member',
        variant: 'primary' as const,
        icon: <Plus className="h-4 w-4" />,
        onClick: () => console.log('Navigate to add member')
      }
    ]
  },
  
  filteredMembers: {
    scenario: 'search' as const,
    titleEs: 'No se encontraron miembros',
    titleEn: 'No members found',
    descriptionEs: 'Intenta ajustar los filtros de búsqueda.',
    descriptionEn: 'Try adjusting the search filters.',
    actions: [
      {
        labelEs: 'Limpiar Filtros',
        labelEn: 'Clear Filters',
        variant: 'secondary' as const,
        icon: <RefreshCw className="h-4 w-4" />,
        onClick: () => console.log('Clear filters')
      }
    ]
  },
  
  // Donations section
  noDonations: {
    scenario: 'donations' as const,
    actions: [
      {
        labelEs: 'Registrar Primera Donación',
        labelEn: 'Record First Donation',
        variant: 'primary' as const,
        icon: <Plus className="h-4 w-4" />,
        onClick: () => console.log('Navigate to add donation')
      }
    ]
  },
  
  monthlyDonations: {
    scenario: 'reports' as const,
    titleEs: 'No hay donaciones este mes',
    titleEn: 'No donations this month',
    descriptionEs: 'No se registraron donaciones en el período seleccionado.',
    descriptionEn: 'No donations were recorded in the selected period.',
    actions: [
      {
        labelEs: 'Ver Mes Anterior',
        labelEn: 'View Previous Month',
        variant: 'secondary' as const,
        icon: <Calendar className="h-4 w-4" />,
        onClick: () => console.log('Change to previous month')
      }
    ]
  },
  
  // Events section
  noEvents: {
    scenario: 'events' as const,
    actions: [
      {
        labelEs: 'Crear Primer Evento',
        labelEn: 'Create First Event',
        variant: 'primary' as const,
        icon: <Plus className="h-4 w-4" />,
        onClick: () => console.log('Navigate to create event')
      }
    ]
  },
  
  upcomingEvents: {
    scenario: 'events' as const,
    titleEs: 'No hay eventos próximos',
    titleEn: 'No upcoming events',
    descriptionEs: 'No hay eventos programados para las próximas semanas.',
    descriptionEn: 'No events scheduled for the coming weeks.',
    actions: [
      {
        labelEs: 'Programar Evento',
        labelEn: 'Schedule Event',
        variant: 'primary' as const,
        icon: <Plus className="h-4 w-4" />,
        onClick: () => console.log('Navigate to schedule event')
      }
    ]
  },
  
  // Reports section
  noReportData: {
    scenario: 'reports' as const,
    titleEs: 'Insuficientes datos para el reporte',
    titleEn: 'Insufficient data for report',
    descriptionEs: 'Necesitas más datos registrados para generar este reporte.',
    descriptionEn: 'You need more data registered to generate this report.',
    helpTextEs: 'Los reportes requieren al menos 30 días de datos para mostrar tendencias significativas.',
    helpTextEn: 'Reports require at least 30 days of data to show meaningful trends.',
    actions: [
      {
        labelEs: 'Ver Datos Disponibles',
        labelEn: 'View Available Data',
        variant: 'secondary' as const,
        icon: <FileText className="h-4 w-4" />,
        onClick: () => console.log('Navigate to data overview')
      }
    ]
  }
}